import React, { useEffect, useState } from "react";
import ModalShell from "../ModalShell";
import { getMessageForFetchError } from "../../shared/utils/apiError";
import {
  saveStockBarchartHoldingsWatchlist,
  updateStockBarchartHoldingsWatchlist,
} from "../../shared/api/stockBarchartHoldingsWatchlists";

/**
 * Add or edit a named Barchart Holdings watchlist (name + URL).
 */
export default function StockBarchartHoldingsWatchlistModal({
  open,
  onClose,
  stockSession,
  currentUserId,
  serverUrl,
  onSaved,
  onError,
  mode = "add",
  watchlistId = null,
  initialName = "",
  initialBarchartUrl = "",
}) {
  const isEdit = mode === "edit";
  const [name, setName] = useState("");
  const [barchartUrl, setBarchartUrl] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(String(initialName || ""));
    setBarchartUrl(String(initialBarchartUrl || ""));
    onError?.("");
  }, [open, initialName, initialBarchartUrl, onError]);

  async function save() {
    const trimmedName = name.trim();
    const url = barchartUrl.trim();
    if (!trimmedName) {
      onError?.("Watchlist name is required.");
      return;
    }
    if (!url) {
      onError?.("Barchart watchlist URL is required.");
      return;
    }
    if (!currentUserId) {
      onError?.("User session is required to save a watchlist.");
      return;
    }

    setSaving(true);
    onError?.("");
    try {
      let data;
      if (isEdit) {
        if (!watchlistId) {
          throw new Error("No watchlist selected.");
        }
        data = await updateStockBarchartHoldingsWatchlist(serverUrl, stockSession, {
          watchlistId,
          name: trimmedName,
          barchartUrl: url,
        });
      } else {
        data = await saveStockBarchartHoldingsWatchlist(serverUrl, stockSession, {
          name: trimmedName,
          barchartUrl: url,
          created_by: currentUserId,
        });
      }
      await onSaved?.(data);
      onClose?.();
    } catch (err) {
      onError?.(getMessageForFetchError(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalShell
      open={open}
      onClose={saving ? undefined : onClose}
      title={isEdit ? "Edit Barchart watchlist" : "Add Barchart watchlist"}
      subtitle="Save a Barchart watchlist URL as a named Holdings tab."
      maxWidth={640}
      footer={
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
          <button type="button" className="btn btn-md btn-secondary" disabled={saving} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-md btn-primary"
            disabled={saving || !name.trim() || !barchartUrl.trim()}
            onClick={save}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      }
    >
      <label style={{ display: "block", marginBottom: 12 }}>
        <span className="form-label">Name</span>
        <input
          type="text"
          className="form-control"
          value={name}
          maxLength={80}
          onChange={(e) => setName(e.target.value)}
          disabled={saving}
          autoFocus
          placeholder="e.g. Core holdings"
        />
      </label>
      <label style={{ display: "block" }}>
        <span className="form-label">Barchart watchlist URL</span>
        <input
          type="url"
          className="form-control"
          value={barchartUrl}
          onChange={(e) => setBarchartUrl(e.target.value)}
          disabled={saving}
          placeholder="https://www.barchart.com/my/watchlist/fullscreen?watchlist=…"
        />
      </label>
      <p className="panel-hint" style={{ marginTop: 8, marginBottom: 0 }}>
        Paste a fullscreen watchlist URL from barchart.com while logged in. Refresh uses these links to
        update Holdings.
      </p>
    </ModalShell>
  );
}
